/**
 * Zwei Fassungen Abschnitt für Abschnitt, mit Rolle und den geänderten
 * Wörtern (seit 27.10.2026, Robert: „sonst weiß niemand, welcher Wortlaut
 * gewonnen hat"). Vorher standen auf /runs/:id beide Texte als Fließtext
 * nebeneinander; bei einem Hero, in dem nur die Subline anders ist, musste
 * man den Unterschied selbst suchen.
 *
 * WOHER DIE ABSCHNITTE KOMMEN: Seit 27.10.2026 legt der Lauf die Gliederung
 * ab, wie die Personas sie gelesen haben (`gliederungFuerAblage` in
 * `supabase/functions/_shared/sim/gliederung.ts`), mit Rolle. Ältere Läufe
 * tragen nur den Text; dann ist jede Zeile ein Abschnitt ohne Rolle. Eine
 * Rolle wird hier nie geraten: eine geratene sähe genauso belastbar aus wie
 * eine gelieferte.
 */

import { t } from './sprache';

export type Rolle = 'headline' | 'subline' | 'ueberschrift' | 'cta' | 'fliesstext' | 'beleg' | 'kleingedrucktes';

const ROLLEN = new Set<string>(['headline', 'subline', 'ueberschrift', 'cta', 'fliesstext', 'beleg', 'kleingedrucktes']);

const ROLLE_DE: Record<Rolle, string> = {
  headline: 'Headline', subline: 'Subline', ueberschrift: 'Zwischenüberschrift', cta: 'Knopf',
  fliesstext: 'Fließtext', beleg: 'Beleg', kleingedrucktes: 'Kleingedrucktes',
};
const ROLLE_EN: Record<Rolle, string> = {
  headline: 'Headline', subline: 'Subline', ueberschrift: 'Subheading', cta: 'Button',
  fliesstext: 'Body copy', beleg: 'Proof', kleingedrucktes: 'Small print',
};

/** Die Rolle in der Sprache der App. */
export const rollenName = (r: Rolle): string => t(ROLLE_DE, ROLLE_EN)[r];

export interface Stueck {
  /** null: der Lauf hat keine Gliederung abgelegt, der Abschnitt ist eine Zeile des Textes. */
  rolle: Rolle | null;
  text: string;
}

/**
 * Die Abschnitte einer Fassung: die abgelegten, wenn es sie gibt, sonst die
 * Zeilen des Textes. Markdown-Rauten am Zeilenanfang fallen, weil die Persona
 * sie auch nicht als Text gelesen hat.
 */
export function stueckeAus(abschnitte: unknown, text: string | null | undefined): Stueck[] {
  if (Array.isArray(abschnitte)) {
    const aus = abschnitte
      .filter((a: any) => a && typeof a.text === 'string' && a.text.trim() && ROLLEN.has(a.rolle))
      .map((a: any) => ({ rolle: a.rolle as Rolle, text: a.text.trim() }));
    if (aus.length) return aus;
  }
  return String(text ?? '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map(z => z.trim().replace(/^#{1,6}\s+/, ''))
    .filter(Boolean)
    .map(z => ({ rolle: null, text: z }));
}

export type Aenderung = 'gleich' | 'geaendert' | 'neu' | 'gestrichen';

export interface Teil {
  text: string;
  /** In der anderen Fassung steht hier etwas anderes. */
  anders: boolean;
}

export interface Zeile {
  aenderung: Aenderung;
  rolle: Rolle | null;
  /** Der Abschnitt in der geprüften Fassung; null, wenn er dort fehlt. */
  neu: string | null;
  /** Der Abschnitt in der Vergleichsfassung; null, wenn er dort fehlt. */
  alt: string | null;
  /** Nur bei „geaendert": beide Texte in Teile zerlegt, die anderen Wörter markiert. */
  woerter?: { neu: Teil[]; alt: Teil[] };
}

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();

/** Längste gemeinsame Teilfolge zweier Listen, als Paare von Indizes. */
function gemeinsam<T>(a: T[], b: T[], gleich: (x: T, y: T) => boolean): Array<[number, number]> {
  const n = a.length, m = b.length;
  const l = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      l[i]![j] = gleich(a[i]!, b[j]!) ? l[i + 1]![j + 1]! + 1 : Math.max(l[i + 1]![j]!, l[i]![j + 1]!);
    }
  }
  const paare: Array<[number, number]> = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (gleich(a[i]!, b[j]!)) { paare.push([i, j]); i++; j++; } else if (l[i + 1]![j]! >= l[i]![j + 1]!) i++; else j++;
  }
  return paare;
}

/**
 * Über so vielen Wörtern je Seite wird nicht mehr Wort für Wort verglichen,
 * dann gilt der Abschnitt als Ganzes als anders. Die Tabelle wächst mit dem
 * Produkt beider Längen, und ein Abschnitt dieser Länge ist ohnehin neu
 * geschrieben, nicht umformuliert.
 */
const MAX_WOERTER = 400;

/** Ein Teil nur aus Satzzeichen. */
const ZEICHEN = /^[.,;:!?…„“”"()[\]]+$/;

/** Die beiden Texte eines geänderten Abschnitts, die anderen Wörter markiert. Leerraum bleibt, wie er war. */
export function wortAbgleich(neu: string, alt: string): { neu: Teil[]; alt: Teil[] } {
  // Satzzeichen sind eigene Teile: sonst gälte „Branchenfaktor." als anders
  // als „Branchenfaktor", nur weil der Satz dort endet.
  const zerlege = (s: string) => s.split(/(\s+|[.,;:!?…„“”"()[\]]+)/).filter(Boolean);
  const a = zerlege(neu), b = zerlege(alt);
  const woerterA = a.map((w, i) => ({ w, i })).filter(x => x.w.trim());
  const woerterB = b.map((w, i) => ({ w, i })).filter(x => x.w.trim());
  if (woerterA.length > MAX_WOERTER || woerterB.length > MAX_WOERTER) {
    return { neu: [{ text: neu, anders: true }], alt: [{ text: alt, anders: true }] };
  }
  const paare = gemeinsam(woerterA, woerterB, (x, y) => norm(x.w) === norm(y.w));
  const gleichA = new Set(paare.map(([i]) => woerterA[i]!.i));
  const gleichB = new Set(paare.map(([, j]) => woerterB[j]!.i));
  const teile = (liste: string[], gleich: Set<number>): Teil[] => {
    const anders = liste.map((w, i) => !!w.trim() && !gleich.has(i));
    // Satzzeichen und Leerraum zwischen zwei anderen Wörtern gehören zur
    // Markierung: sonst zerfällt „500.000" in drei Stücke, nur weil der Punkt
    // auch in der anderen Fassung vorkommt.
    const nachbar = (i: number, schritt: number) => {
      for (let k = i + schritt; k >= 0 && k < liste.length; k += schritt) if (liste[k]!.trim() && !ZEICHEN.test(liste[k]!)) return anders[k]!;
      return false;
    };
    liste.forEach((w, i) => {
      if ((!w.trim() || ZEICHEN.test(w)) && nachbar(i, -1) && nachbar(i, 1)) anders[i] = true;
    });
    const aus: Teil[] = [];
    liste.forEach((w, i) => {
      const letzte = aus[aus.length - 1];
      if (letzte && letzte.anders === anders[i]) letzte.text += w; else aus.push({ text: w, anders: anders[i]! });
    });
    return aus;
  };
  return { neu: teile(a, gleichA), alt: teile(b, gleichB) };
}

/**
 * Die geprüfte Fassung neben der Vergleichsfassung, Abschnitt für Abschnitt.
 * Gleiche Abschnitte verankern die Reihenfolge (längste gemeinsame
 * Teilfolge). Dazwischen paart sich, was dieselbe Rolle hat, in der
 * Reihenfolge der Seite: das ist „geändert". Was übrig bleibt, ist „neu" oder
 * „gestrichen" und steht an seiner Stelle.
 */
export function gleicheAb(neu: Stueck[], alt: Stueck[]): Zeile[] {
  const anker = gemeinsam(neu, alt, (x, y) => x.rolle === y.rolle && norm(x.text) === norm(y.text));
  const aus: Zeile[] = [];
  const luecke = (n0: number, n1: number, a0: number, a1: number) => {
    const frei = new Set<number>();
    for (let j = a0; j < a1; j++) frei.add(j);
    let naechsteAlt = a0;
    const gestrichenBis = (bis: number) => {
      for (; naechsteAlt < bis; naechsteAlt++) {
        if (frei.has(naechsteAlt)) {
          frei.delete(naechsteAlt);
          aus.push({ aenderung: 'gestrichen', rolle: alt[naechsteAlt]!.rolle, neu: null, alt: alt[naechsteAlt]!.text });
        }
      }
    };
    for (let i = n0; i < n1; i++) {
      const s = neu[i]!;
      let partner = -1;
      for (let j = naechsteAlt; j < a1; j++) if (frei.has(j) && alt[j]!.rolle === s.rolle) { partner = j; break; }
      if (partner < 0) {
        aus.push({ aenderung: 'neu', rolle: s.rolle, neu: s.text, alt: null });
        continue;
      }
      gestrichenBis(partner);
      frei.delete(partner);
      naechsteAlt = partner + 1;
      aus.push({ aenderung: 'geaendert', rolle: s.rolle, neu: s.text, alt: alt[partner]!.text, woerter: wortAbgleich(s.text, alt[partner]!.text) });
    }
    gestrichenBis(a1);
  };
  let ni = 0, ai = 0;
  for (const [i, j] of anker) {
    luecke(ni, i, ai, j);
    aus.push({ aenderung: 'gleich', rolle: neu[i]!.rolle, neu: neu[i]!.text, alt: alt[j]!.text });
    ni = i + 1;
    ai = j + 1;
  }
  luecke(ni, neu.length, ai, alt.length);
  return aus;
}

/** Wie viele Abschnitte sich unterscheiden; für die Zeile über dem Abgleich. */
export const geaenderte = (zeilen: Zeile[]): number => zeilen.filter(z => z.aenderung !== 'gleich').length;

// ─── Im Bericht ──────────────────────────────────────────────────────────────

/**
 * Für den Studienbericht (Leseansicht in `seiten/Bericht.tsx`): eine Fassung
 * als Zeilen in der Form, die der Bericht schon zeichnet. Trägt der Run eine
 * Gliederung, oder hat der Text keine Überschriften, an denen
 * `vergleicheAbschnitte` sich festhalten kann (ein Hero sind Zeilen, keine
 * Kapitel), dann gilt der Abgleich hier: je Abschnitt mit Rolle, die anderen
 * Wörter markiert. Sonst null, und der Bericht vergleicht wie bisher nach
 * Überschriften.
 */
export interface BerichtZeile {
  kopf: string;
  aenderung: 'gleich' | 'umformuliert' | 'neu' | 'gestrichen' | null;
  neu: { kopf: string; rumpf: string } | null;
  alt: { kopf: string; rumpf: string } | null;
  woerter?: { neu: Teil[]; alt: Teil[] };
  /** Die Rolle in der Sprache der App, links an der Zeile. Fehlt ohne Gliederung. */
  rolle?: string;
}

const UEBERSCHRIFT = /^\s*(#{1,4}\s+\S|[A-ZÄÖÜ][A-ZÄÖÜ_ -]{2,30}:)/m;

export function berichtsZeilen(x: any, daneben: boolean): BerichtZeile[] | null {
  const text = typeof x?.text === 'string' ? x.text : '';
  const alt = daneben && typeof x?.baseline_text === 'string' ? x.baseline_text : '';
  const gegliedert = Array.isArray(x?.abschnitte) && x.abschnitte.length > 0;
  if (!gegliedert && (UEBERSCHRIFT.test(text) || UEBERSCHRIFT.test(alt))) return null;
  const stueck = (s: string) => ({ kopf: '', rumpf: s });
  const rolle = (r: Rolle | null) => (r ? { rolle: rollenName(r) } : {});
  const neu = stueckeAus(x?.abschnitte, text);
  if (!daneben || !alt) return neu.map(s => ({ kopf: '', aenderung: null, neu: stueck(s.text), alt: null, ...rolle(s.rolle) }));
  return gleicheAb(neu, stueckeAus(x?.baseline_abschnitte, alt)).map(z => ({
    kopf: '',
    aenderung: z.aenderung === 'geaendert' ? 'umformuliert' : z.aenderung,
    neu: z.neu == null ? null : stueck(z.neu),
    alt: z.alt == null ? null : stueck(z.alt),
    ...rolle(z.rolle),
    ...(z.woerter ? { woerter: z.woerter } : {}),
  }));
}
